import React, { useMemo, useState } from 'react';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import {
  priorities as allPriorities,
  PRIORITY_IDS
} from 'common/components/AdvancedSearch/AdvancedSearch.constants';
import { PrioritySelector } from 'common/components/AdvancedSearch/components';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import { SECONDARY_API_CLIENT } from 'services/API';
import strings from 'strings';
import TextBox from 'UI/components/atoms/TextBox';
import { When } from 'UI/components/atoms/When';

import { CollapsibleChipFilter } from '../CollapsibleChipFilter';

import { useStyles } from './SalaryFilter.styled';

const SALARY_PRIORITIES = allPriorities.filter(p => p.id !== PRIORITY_IDS.DoesntHave);
const SALARY_TYPE_ID = 'salary-type';
const EMPTY_VALUE = { type: null, min: null, max: null, priority: PRIORITY_IDS.CanHave };

const getPriorityLabel = id => (SALARY_PRIORITIES.find(p => p.id === id) || {}).shortLabel || '';

export const SalaryFilter = ({
  name,
  title,
  fieldName,
  salaryTypeFieldName,
  endpoint,
  value,
  onChange
}) => {
  const classes = useStyles();
  const [priority, setPriority] = useState(value?.priority ?? EMPTY_VALUE.priority);
  const currentValue = value ?? { ...EMPTY_VALUE, priority };
  const currentType = currentValue.type;

  const { state: salaryTypesState } = useFetchWithStatusV2({
    endpoint: `${endpoint}?size=25`,
    apiInstance: SECONDARY_API_CLIENT
  });
  const salaryTypeOptions = useMemo(() => {
    const raw = salaryTypesState.results;
    return Array.isArray(raw) ? raw : raw?.data || [];
  }, [salaryTypesState.results]);

  const notify = nextPartial => {
    const next = { ...EMPTY_VALUE, ...currentValue, ...nextPartial };
    const hasAny = next.type !== null || next.min !== null || next.max !== null;
    if (!hasAny) setPriority(next.priority);
    onChange(name, { fieldName, salaryTypeFieldName, value: hasAny ? next : null });
  };

  const handleClear = () => {
    onChange(name, { fieldName, salaryTypeFieldName, value: null });
  };

  const handleTypeChange = event => {
    notify({ type: event.target.value || null });
  };

  const handleMinChange = (_key, val) => {
    notify({ min: val !== '' ? val : null });
  };

  const handleMaxChange = (_key, val) => {
    notify({ max: val !== '' ? val : null });
  };

  const pseudoItem = currentType
    ? { id: SALARY_TYPE_ID, item: { value: currentType }, priority: currentValue.priority }
    : null;

  return (
    <CollapsibleChipFilter
      title={title}
      showClear={value !== null}
      onClear={handleClear}
      chipItem={pseudoItem}
      getChipLabel={i => i.value}
      getChipPriorityLabel={getPriorityLabel}
      priorities={SALARY_PRIORITIES}
      onPriorityChange={p => notify({ priority: p })}
      triggerLabel={strings.resumeSearch.filters.salary.chooseTypeAndRange}
    >
      {({ isExpanded }) => (
        <>
          <When condition={isExpanded}>
            <div className={classes.selectorsRow}>
              <FormControl className={classes.typeSelect} size="small" variant="outlined">
                <InputLabel>{strings.resumeSearch.filters.salary.typeLabel}</InputLabel>
                <Select
                  label={strings.resumeSearch.filters.salary.typeLabel}
                  value={currentType || ''}
                  onChange={handleTypeChange}
                >
                  {salaryTypeOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <PrioritySelector
                priorities={SALARY_PRIORITIES}
                selectedPriority={currentValue.priority || PRIORITY_IDS.CanHave}
                onPriorityChange={p => notify({ priority: p })}
                size="small"
              />
            </div>
          </When>

          <When condition={currentType}>
            <div className={classes.rangeContainer}>
              <TextBox
                disableDecimals
                disableNegatives
                inputType="currency"
                label={strings.resumeSearch.filters.salary.min}
                name={`${name}Min`}
                onChange={handleMinChange}
                value={currentValue.min ?? ''}
              />
              <TextBox
                disableDecimals
                disableNegatives
                inputType="currency"
                label={strings.resumeSearch.filters.salary.max}
                name={`${name}Max`}
                onChange={handleMaxChange}
                value={currentValue.max ?? ''}
              />
            </div>
          </When>
        </>
      )}
    </CollapsibleChipFilter>
  );
};
